import { Calendar, CheckCircle2, ExternalLink, Globe, Lock, Users } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import PerformanceChartModal from "../components/PerformanceChartModal";
import { DisplayPortfolioData, PortfolioDisplay } from "../components/portfolio/display";
import {
  AccessShareableLinkResponse,
  shareableLinksService,
} from "../services/shareableLinksService";
import { trackShareableLinkVisit } from "../utils/analytics";
import { formatUtcDate } from "../utils/dateUtils";

interface SharePortfolioPageProps {
  initialResponse?: AccessShareableLinkResponse | null;
}

const formatDate = (dateString: string): string => {
  return formatUtcDate(dateString, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
};

const getSignupUrlWithUTM = (portfolio: DisplayPortfolioData | null): string => {
  const params = new URLSearchParams();
  params.set("utm_source", "shared_link");
  params.set("utm_medium", "portfolio");
  if (portfolio) {
    params.set("utm_campaign", portfolio.provider?.name || portfolio.providers?.name || "");
    params.set("utm_content", portfolio.name);
  }
  return `/signup?${params.toString()}`;
};

// Private portfolio view
const PrivatePortfolioView: React.FC<{ portfolio: DisplayPortfolioData }> = ({ portfolio }) => {
  const providerName = portfolio.provider?.name || portfolio.providers?.name;
  const signupUrl = getSignupUrlWithUTM(portfolio);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => window.open(signupUrl, "_blank")}
              className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            >
              <img src="/favicon.png" alt="Plutus" className="h-12 w-12" />
              <img src="/logo.png" alt="Plutus" className="h-9 w-auto" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 sm:p-8 shadow-xl border border-gray-200/50">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto bg-gray-100 border border-gray-200">
              <Lock className="w-8 h-8 text-gray-500" />
            </div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3">{portfolio.name}</h1>
            <div className="flex items-center justify-center space-x-2 text-gray-600 mb-6">
              <Users className="w-4 h-4" />
              <span className="text-sm font-medium">Shared by {providerName}</span>
            </div>
            {portfolio.description && (
              <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-6">{portfolio.description}</p>
            )}
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
              <p className="text-red-600 font-medium">{portfolio.message}</p>
            </div>
            <ConnectBrokerCTA signupUrl={signupUrl} />
          </div>
        </div>
      </div>
    </div>
  );
};

// Connect Broker CTA component
const ConnectBrokerCTA: React.FC<{ signupUrl: string }> = ({ signupUrl }) => (
  <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-center text-white shadow-xl">
    <div className="mb-6">
      <h3 className="text-xl sm:text-2xl font-bold mb-3">Explore other model portfolios and invest automatically</h3>
      <p className="text-teal-100 text-sm sm:text-base max-w-2xl mx-auto leading-relaxed">
        Connect your broker and start investing in professionally managed strategies with automated execution.
      </p>
    </div>

    <div className="flex items-center justify-center mb-6">
      <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-xl px-4 py-3 border border-white/20">
        <img src="/ibkr.png" alt="Interactive Brokers" className="h-8 w-auto" />
        <span className="text-white font-medium">Interactive Brokers</span>
      </div>
    </div>

    <button
      onClick={() => window.open(signupUrl, "_blank")}
      className="bg-white text-teal-700 font-bold py-3 px-8 rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl"
    >
      Connect Broker & Invest
    </button>
  </div>
);

// Plutus branding footer
const PlutusBrandingFooter: React.FC<{ signupUrl: string }> = ({ signupUrl }) => (
  <div className="mt-8 text-center pb-8">
    <button
      onClick={() => window.open(signupUrl, "_blank")}
      className="inline-flex items-center space-x-3 hover:opacity-80 transition-opacity group"
    >
      <img src="/favicon.png" alt="Plutus" className="h-12 w-12 group-hover:scale-105 transition-transform" />
      <img src="/logo.png" alt="Plutus" className="h-8 w-auto group-hover:scale-105 transition-transform" />
    </button>
  </div>
);

// Header with verified badge
const SharePortfolioHeader: React.FC<{ signupUrl: string }> = ({ signupUrl }) => (
  <div className="bg-white/80 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-50">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="flex items-center justify-between">
        <button
          onClick={() => window.open(signupUrl, "_blank")}
          className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
        >
          <img src="/favicon.png" alt="Plutus" className="h-12 w-12" />
          <img src="/logo.png" alt="Plutus" className="h-9 w-auto" />
        </button>
        <div className="flex items-center space-x-3">
          <div
            className="px-4 py-2 rounded-full text-sm font-semibold bg-green-600 text-white border border-green-700 shadow-sm cursor-help relative group"
            title="This portfolio was created on Plutus and is certified to be accurate"
          >
            <CheckCircle2 className="w-4 h-4 inline mr-1.5" />
            Verified
            <div className="absolute hidden group-hover:block top-full right-0 mt-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl z-50">
              <div className="absolute -top-1 right-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
              This portfolio was created on Plutus and is certified to be accurate.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Meta info section
const MetaInfoSection: React.FC<{ portfolio: DisplayPortfolioData }> = ({ portfolio }) => (
  <div className="mt-6 pt-6 border-t border-gray-200">
    <div className="flex items-center justify-center space-x-6 text-sm text-gray-500">
      <div className="flex items-center space-x-2">
        <Calendar className="w-4 h-4" />
        <span>Created {formatDate(portfolio.createdAt)}</span>
      </div>
      <div className="flex items-center space-x-2">
        <Globe className="w-4 h-4" />
        <span>Public Portfolio</span>
      </div>
    </div>
  </div>
);

const SharePortfolioPage: React.FC<SharePortfolioPageProps> = ({ initialResponse = null }) => {
  const { url, token } = useParams<{ url?: string; token?: string }>();
  const shareToken = url ?? token;
  const [portfolioData, setPortfolioData] = useState<DisplayPortfolioData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPerformanceModal, setShowPerformanceModal] = useState(false);

  const applySharedPortfolioResponse = useCallback(async (response: AccessShareableLinkResponse) => {
    if (response.type !== "PORTFOLIO") {
      setPortfolioData(null);
      setError("This link is not for a portfolio");
      return;
    }

    const nextPortfolioData = response.resourceData as DisplayPortfolioData;

    // Fetch provider name from public endpoint if not already present.
    if (!nextPortfolioData.provider?.name && !nextPortfolioData.providers?.name && response.providerId) {
      const providerInfo = await shareableLinksService.getPublicProviderInfo(response.providerId);
      if (providerInfo) {
        nextPortfolioData.provider = providerInfo;
      }
    }

    setError(null);
    setPortfolioData(nextPortfolioData);
    trackShareableLinkVisit(
      "PORTFOLIO",
      nextPortfolioData.id,
      nextPortfolioData.name,
      nextPortfolioData.provider?.name || nextPortfolioData.providers?.name || "Unknown"
    );
  }, []);

  const loadSharedPortfolio = useCallback(async (resolvedShareToken: string) => {
    try {
      setLoading(true);
      setError(null);

      const response = await shareableLinksService.accessShareableLink(resolvedShareToken);
      await applySharedPortfolioResponse(response);
    } catch (err) {
      setPortfolioData(null);
      console.error("Failed to load shared portfolio:", err);
      if (err instanceof Error) {
        if (err.message.includes("not found")) {
          setError("This shareable link is not found or has been removed");
        } else if (err.message.includes("inactive") || err.message.includes("expired")) {
          setError("This shareable link is no longer active");
        } else {
          setError("Failed to load the shared portfolio. Please try again later.");
        }
      } else {
        setError("An unexpected error occurred");
      }
    } finally {
      setLoading(false);
    }
  }, [applySharedPortfolioResponse]);

  useEffect(() => {
    if (initialResponse) {
      setLoading(true);
      setError(null);
      void applySharedPortfolioResponse(initialResponse).finally(() => {
        setLoading(false);
      });
      return;
    }

    if (shareToken) {
      void loadSharedPortfolio(shareToken);
    }
  }, [applySharedPortfolioResponse, initialResponse, loadSharedPortfolio, shareToken]);

  const signupUrl = getSignupUrlWithUTM(portfolioData);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
        <div className="text-center bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200/50">
          <div className="mb-6">
            <img src="/favicon.png" alt="Plutus" className="h-16 w-16 mx-auto animate-pulse" />
            <img src="/logo.png" alt="Plutus" className="h-12 w-auto mx-auto mt-2" />
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading portfolio...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-200/50">
            <div className="mb-6">
              <img src="/favicon.png" alt="Plutus" className="h-16 w-16 mx-auto mb-2" />
              <img src="/logo.png" alt="Plutus" className="h-12 w-auto mx-auto" />
            </div>
            <div className="w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto bg-red-50 border border-red-200">
              <ExternalLink className="w-8 h-8 text-red-500" />
            </div>
            <h1 className="text-2xl font-bold mb-4 text-gray-900">Portfolio Not Available</h1>
            <p className="mb-6 text-gray-600 leading-relaxed">{error}</p>
            <div className="space-y-3">
              <button
                onClick={() => window.open(signupUrl, "_blank")}
                className="w-full bg-teal-600 text-white font-medium py-3 px-6 rounded-xl hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Explore Plutus
              </button>
              <p className="text-sm text-gray-500">Discover investment strategies from top providers</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!portfolioData) {
    return null;
  }

  // Handle private portfolio
  if (portfolioData.isPrivate) {
    return <PrivatePortfolioView portfolio={portfolioData} />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100">
      <SharePortfolioHeader signupUrl={signupUrl} />

      <PortfolioDisplay
        portfolio={portfolioData}
        performanceMetrics={portfolioData.performanceMetrics}
        isDarkMode={false}
        forceLightMode={true}
        showBackButton={false}
        showWatermark={true}
        showProviderInfo={true}
        renderAfterChart={() => (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <MetaInfoSection portfolio={portfolioData} />
          </div>
        )}
        renderAfterComposition={() => (
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <ConnectBrokerCTA signupUrl={signupUrl} />
          </div>
        )}
        renderFooter={() => <PlutusBrandingFooter signupUrl={signupUrl} />}
      />

      {/* Performance Chart Modal */}
      {portfolioData && showPerformanceModal && (
        <PerformanceChartModal
          isOpen={showPerformanceModal}
          onClose={() => setShowPerformanceModal(false)}
          entityId={portfolioData.id}
          entityType="PORTFOLIO"
          entityName={portfolioData.name}
          forceLightMode={true}
        />
      )}
    </div>
  );
};

export default SharePortfolioPage;
